import React, { FC } from 'react';
import { colors } from 'styles/colors';
import { BadgesNames } from 'models/badges';
import { Text } from 'components/text/Text';
import styled from 'styled-components';
import { getBadgeIcon } from 'assets/icons/badges/badge-icons';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { Stack } from 'components/stack/stack';

export type RatingBadgeProps = {
  badge: BadgesNames;
  size?: number;
  count?: number;
  isSelectable?: boolean;
  selected?: boolean;
  onClick?: (badge: BadgesNames) => void;
};

const BadgeWrapper = styled(Stack)<{ selected?: boolean; isSelectable?: boolean; size: number }>`
  display: flex;
  width: ${({ size }) => size + 44}px;
  min-width: ${({ size }) => size + 44}px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  opacity: ${({ isSelectable, selected }) =>
    !isSelectable || (isSelectable && selected) ? 1 : 0.6};
  cursor: ${({ isSelectable }) => (isSelectable ? 'pointer' : 'unset')};
  &:hover {
    opacity: 1;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const Img = styled.img<{ size: number }>`
  position: relative;
  height: ${({ size }) => size}px;
  width: ${({ size }) => size}px;
`;

const CountBox = styled.div`
  position: absolute;
  top: 0;
  right: -5px;
  background-color: ${colors.primary};
  height: 17px;
  min-width: 26px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
`;

export const RatingBadge: FC<RatingBadgeProps> = ({
  badge,
  onClick,
  selected,
  count,
  size = 56,
  isSelectable,
}) => {
  const translation = getBadgeTranslationKey(badge);

  return (
    <BadgeWrapper
      onClick={() => onClick && onClick(badge)}
      selected={selected}
      isSelectable={isSelectable}
      size={size}
    >
      <ImageWrapper>
        {count && (
          <CountBox>
            <Text color={'white'} textAlign={'center'} size={10}>
              {count}
            </Text>
          </CountBox>
        )}
        <Img src={getBadgeIcon(badge)} alt={translation} size={size} loading="lazy" />
      </ImageWrapper>
      <Text size={14}>{translate(translation)}</Text>
    </BadgeWrapper>
  );
};

const getBadgeTranslationKey = (badge: BadgesNames) => {
  const {
    communication,
    excellent,
    expert,
    flexible,
    fun,
    motivator,
  } = translationKeys.ratings.badges;

  switch (badge) {
    case 'COMMUNICATION':
      return communication;
    case 'EXCELLENT':
      return excellent;
    case 'EXPERT':
      return expert;
    case 'FLEXIBLE':
      return flexible;
    case 'FUN_LEARNING':
      return fun;
    case 'MOTIVATOR':
      return motivator;
    default:
      throw new Error('Invalid translation key');
  }
};
